import React, { FC, ReactNode } from "react";
import Image from "next/image";
import Customiser from "./Customiser";
import { FlexRow } from "ui-kit/Layout/Flex";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { useWebTranslation } from "translations";
import { formatCurrency, NON_BREAKING_SPACE } from "utils/utils";

const InfoText = ({
  title,
  onClick,
  isSubtitle,
}: {
  title: string;
  onClick?: () => void;
  isSubtitle?: boolean;
}) => (
  <>
    {isSubtitle && <Gutter size={3} />}
    <FlexRow alignItems="center">
      <Typography variant={isSubtitle ? "h4" : "h3"} color="grey500">
        {title}
      </Typography>
      {onClick && (
        <>
          <Gutter size={1} horizontal />
          <Image
            src="/images/icons/question-circle.svg"
            alt="external-link"
            width={20}
            height={20}
            style={{
              display: "flex",
              cursor: "pointer",
            }}
            onClick={() => onClick()}
          />
        </>
      )}
    </FlexRow>
  </>
);
type ChooseDeductibleProps = {
  currentTotal: number;
  currentDeductible: number;
  currentCopay: number;
  deductibles: { value: number; total: number }[];
  copays: { value: number; total: number }[];
  onChangeDeductibleCopay: (deductible: number, copay: number) => void;
  onInfo: ({ title, content }: { title: string; content: ReactNode }) => void;
  deductibleDescription?: string;
  copayDescription?: string;
  inverseCopayDisplay?: boolean;
};

const ChooseDeductible: FC<ChooseDeductibleProps> = ({
  currentTotal,
  currentCopay,
  currentDeductible,
  deductibles,
  copays,
  onChangeDeductibleCopay,
  onInfo,
  deductibleDescription,
  copayDescription,
  inverseCopayDisplay,
}) => {
  const { t } = useWebTranslation();
  const isMultiDeductible = !!(deductibles.length && copays.length);

  return (
    <>
      {isMultiDeductible && (
        <>
          <InfoText
            title={t("global.deductible")}
            onClick={() =>
              onInfo({
                title: t("global.deductible"),
                content: (
                  <Typography style={{ whiteSpace: "pre-wrap" }}>
                    {t("quote_preview.deductible_description")}
                  </Typography>
                ),
              })
            }
          />
          <Gutter size={3} />
          <Typography>{t("quote_preview.deductible")}</Typography>
        </>
      )}
      {deductibles.length > 0 && (
        <>
          <InfoText
            title={t("global.fixed_deductible")}
            onClick={() =>
              onInfo({
                title: t("global.fixed_deductible"),
                content: (
                  <Typography style={{ whiteSpace: "pre-wrap" }}>
                    {t("global.fixed_deductible_description")}
                  </Typography>
                ),
              })
            }
            isSubtitle={isMultiDeductible}
          />
          {deductibleDescription && (
            <>
              <Gutter size={1} />
              <Typography variant="body3">{deductibleDescription}</Typography>
            </>
          )}
          <Gutter size={3} />
          <Customiser
            options={deductibles.map(({ value, total }) => ({
              value,
              title: formatCurrency(value / 100),
              totalDiff: total - currentTotal,
            }))}
            value={currentDeductible}
            onChange={(deductible) =>
              onChangeDeductibleCopay(deductible, currentCopay)
            }
            direction="column"
          />
        </>
      )}
      {copays.length > 0 && (
        <>
          <InfoText
            title={t("global.copay")}
            onClick={() =>
              onInfo({
                title: t("global.copay"),
                content: (
                  <Typography style={{ whiteSpace: "pre-wrap" }}>
                    {t("global.copay_description")}
                  </Typography>
                ),
              })
            }
            isSubtitle={isMultiDeductible}
          />
          {copayDescription && (
            <>
              <Gutter size={1} />
              <Typography variant="body3">{copayDescription}</Typography>
            </>
          )}
          <Gutter size={3} />
          <Customiser
            options={copays.map(({ value, total }) => {
              return {
                value,
                title: `${
                  (inverseCopayDisplay ? 1 - value : value) * 100
                }${NON_BREAKING_SPACE}%`,
                totalDiff: total - currentTotal,
              };
            })}
            value={currentCopay}
            onChange={(copay) =>
              onChangeDeductibleCopay(currentDeductible, copay)
            }
          />
        </>
      )}
    </>
  );
};

export default ChooseDeductible;
